<template>
  <RfSimpleCard>
    <div class="user-setting-content settings-all-dwnld-wrap">
      <div class="flex flex-col gap-5 pb-5">
        <div>
          All raw prompt interaction events get be downloaded from your dedicated AWS S3 bucket.
          <RfButton
            buttonText="Click here for credentials."
            link
            class="pb-1"
            :disabled="userStore.isDisabledWithMessage"
            @click="awsCredentialsModal?.show()"
          />
          Refer to the
          <a target="_blank" href="https://docs.recurly.com/recurly-engage/docs/download-data">
            documentation for more information.
          </a>
        </div>

        <div>
          Download detailed prompt interaction data at a per userid level for the specified date
          range. Please note this may take several minutes to complete. Exports can be downloaded
          for 30 days.
        </div>
        <RfAwsCredentialsModal
          ref="awsCredentialsModal"
          :loading="appsStore.currAppAwsCredentials.loading"
          :awsUserName="currApp.aws_creds?.user_name"
          :awsPath="currApp.aws_creds?.aws_path.replace('ingest', 'exports')"
          :awsAccessKey="
            appsStore.currAppAwsCredentials.value?.access_key_id ||
            currApp?.aws_creds?.access_key_id
          "
          :awsSecretKey="
            appsStore.currAppAwsCredentials.value?.secret_access_key ||
            currApp?.aws_creds?.secret_access_key
          "
          @update:showCredentials="
            value =>
              value
                ? appsStore.getAppAwsCredentials(currApp.id)
                : (appsStore.currAppAwsCredentials.value = {})
          "
        />
      </div>
      <div class="settings-download-wrapper">
        <div class="settings-download-ctr">
          <div class="stg-dwnld-label">Select date range</div>
          <RfMetricsDateRangeSelector
            parentPage="segments"
            @fetchMetrics="fetchMetrics"
            @fetchMetricsCustom="fetchMetricsCustom"
          />
        </div>
        <div class="settings-download-ctr">
          <div class="stg-dwnld-label">Export Type</div>
          <v-radio-group v-model="exportType" inline>
            <v-radio label="Prompt Activities" value="path_activities"></v-radio>
            <v-radio label="Experiment Metrics" value="experiment_metrics"></v-radio>
          </v-radio-group>
        </div>
        <div class="stg-dwnld-btns">
          <v-btn variant="flat" color="accent" @click="exportToCsv">
            <v-icon size="20" start>md:download</v-icon>Export CSV</v-btn
          >
        </div>
      </div>
      <v-table class="rf-settings-table-data mt-5">
        <template #default>
          <thead>
            <tr>
              <th class="text-left">Title</th>
              <th class="text-left">Date Requested</th>
              <th class="text-left">Requested By</th>
              <th class="text-left">Status</th>
              <th class="text-left">Download</th>
            </tr>
          </thead>
          <tbody v-if="jobsStore.jobs && jobsStore.jobs.length > 0">
            <tr v-for="(job, index) in jobsStore.jobs" :key="index">
              <td>
                {{ jobTitle(job) }} - {{ dateDisplay(job.start_date, { alter: true }) }} -
                {{ dateDisplay(job.end_date, { alter: true }) }}
              </td>
              <td>{{ dateDisplay(job.created_at, { alter: true, time: true }) }}</td>
              <td>{{ job.creator_name }}</td>
              <td>{{ job.status }}{{ timeElapsed(job) }}</td>
              <td>
                <RfButton
                  :disabled="job.status !== 'completed'"
                  icon="md:download"
                  variant="text"
                  color="accent"
                  @click="downloadLink(job)"
                />
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr>
              <td colspan="5">
                <div class="settings-no-data-message">Nothing to show.</div>
              </td>
            </tr>
          </tbody>
        </template>
      </v-table>
    </div>
  </RfSimpleCard>
</template>

<script>
import dayjs from "dayjs";
import RfMetricsDateRangeSelector from "@/components/RfCommonCards/RfMetricsDateRangeSelector.vue";
import RfAwsCredentialsModal from "@/blocks/RfModals/RfAwsCredentialsModal.vue";
import ApiJobs from "@/apis/ApiJobs";
import { saveLinkToCsv } from "@/utils/CsvUtils";
import { dateDisplay } from "@/utils/DateDisplayUtils";
import { useJobsStore } from "@/pinia/jobsStore";
import { watch, onMounted, onUnmounted, ref, computed } from "vue";
import { useAppsStore } from "@/pinia/appsStore";
import { useUserStore } from "@/pinia/userStore";
import { useRoute } from "vue-router";
import RfButton from "../buttons/RfButton.vue";
import RfSimpleCard from "../RfSimpleCard.vue";

export default {
  name: "RfDownloadSettings",
  components: {
    RfMetricsDateRangeSelector,
    RfSimpleCard,
    RfAwsCredentialsModal,
    RfButton,
  },
  setup: () => {
    const route = useRoute();
    const jobsStore = useJobsStore();
    const appsStore = useAppsStore();
    const intervalId = ref(null);
    const intervalMs = ref(3000);
    const currChartSrc = ref(null);
    const singleDayCustom = ref(false);
    const dateRange = ref(null);
    const lastCheckedDateTime = ref(null);
    const exportType = ref("path_activities");

    watch(
      () => jobsStore.activeJobs,
      jobs => {
        if (jobs.length > 0) {
          if (!intervalId.value) {
            intervalId.value = setInterval(() => {
              jobsStore.getJobs(route.params.aid);
              lastCheckedDateTime.value = dayjs();
            }, intervalMs.value);
          }
        } else {
          clearInterval(intervalId.value);
          intervalId.value = null;
        }
      },
    );

    onMounted(() => jobsStore.getJobs(route.params.aid));

    onUnmounted(() => {
      clearInterval(intervalId.value);
      intervalId.value = null;
    });

    return {
      awsCredentialsModal: ref(),
      dateDisplay,
      jobsStore,
      appsStore,
      currChartSrc,
      singleDayCustom,
      dateRange,
      lastCheckedDateTime,
      exportType,
      currApp: computed(() => appsStore.app),
      userStore: useUserStore(),
    };
  },
  methods: {
    timeElapsed(job) {
      if (job.status === "pending" || (job.status === "processing" && this.lastCheckedDateTime)) {
        let minutesDiff = dayjs(job.created_at).diff(this.lastCheckedDateTime, "minute") * -1;
        if (minutesDiff < 0 || isNaN(minutesDiff)) {
          minutesDiff = 0;
        }
        return ` (${minutesDiff}min elapsed)`;
      }
      return "";
    },
    exportToCsv() {
      const metricsPeriod = { period: this.currChartSrc };
      if (this.currChartSrc === "custom") {
        metricsPeriod.date_range = this.date_range;
      }
      this.lastCheckedDateTime = dayjs();
      this.jobsStore.createJob({
        appId: this.currApp.id,
        metrics_period: metricsPeriod,
        export_type: this.exportType,
      });
    },
    fetchMetrics(timePeriod) {
      this.date_range = timePeriod;
      this.currChartSrc = timePeriod;
    },
    fetchMetricsCustom(timePeriod) {
      this.date_range = timePeriod;
      this.currChartSrc = "custom";
    },
    async downloadLink(job) {
      const dlLink = await ApiJobs.downloadJobLink(this.currApp.id, job);
      const { link } = dlLink;
      const startDate = dayjs(job.start_date).format("MM_D_YYYY");
      const endDate = dayjs(job.end_date).format("MM_D_YYYY");
      const fname = `${startDate}_${endDate}_activities`;
      saveLinkToCsv(link, "activities", fname);
    },
    jobTitle(job) {
      if (job.export_type === "experiment_metrics") {
        return "Experiment metrics data";
      }
      return "Prompt user interactions data";
    },
  },
};
</script>
